<template>
  <header class="cabecera-movil">
    <div class="cabecera-movil__barra">
      <div class="cabecera-movil__izquierda">
        <button
          v-if="!esInicio"
          class="cabecera-movil__boton"
          type="button"
          :aria-label="$t('header.menu.volver')"
          @click="router.back()"
        >
          <IconArrowLeft :size="22" stroke-width="2" />
        </button>
        <button
          class="cabecera-movil__boton"
          type="button"
          aria-controls="menu-movil"
          :aria-expanded="abierto"
          :aria-label="abierto ? $t('header.menu.cerrar') : $t('header.menu.abrir')"
          @click="abierto = !abierto"
        >
          <IconX v-if="abierto" :size="24" stroke-width="2" />
          <IconDotsVertical v-else :size="24" stroke-width="2" />
        </button>
        <img
          class="cabecera-movil__espacio"
          src="@/assets/img/espacio_movistar_logo.png"
          alt="Espacio Movistar"
          @click="router.push({ name: 'Home' })"
        />
      </div>

      <img
        class="cabecera-movil__logo"
        src="@/assets/img/logo.svg"
        alt="Movistar"
        @click="router.push({ name: 'Home' })"
      />

      <LanguageChange
        class="cabecera-movil__idioma"
        flexJustify="flex-end"
        :showLogo="false"
        color="#fff"
        padding="0px"
      />
    </div>

    <nav v-show="abierto" id="menu-movil" class="cabecera-movil__menu">
      <button
        type="button"
        :class="['cabecera-movil__opcion', { 'cabecera-movil__opcion--activa': route.name == 'Home' }]"
        @click="elegir('descubre')"
      >
        {{ $t('header.menu.discover') }}
      </button>
      <button
        type="button"
        :class="['cabecera-movil__opcion', { 'cabecera-movil__opcion--activa': route.name == 'ExperienceTypes' }]"
        @click="elegir('agenda')"
      >
        {{ $t('header.menu.agenda') }}
      </button>
      <button
        v-for="tipo in tipos"
        :key="tipo.experienceTypeId"
        type="button"
        :class="['cabecera-movil__opcion', { 'cabecera-movil__opcion--activa': route.params.experienceTypeCode == tipo.code }]"
        @click="elegir('tipo', tipo)"
      >
        {{ translate(tipo.title) }}
      </button>
      <button
        type="button"
        class="cabecera-movil__opcion"
        @click="elegir('atencion')"
      >
        {{ $t('header.menu.commercial-attention') }}
      </button>
    </nav>
  </header>
  <div v-if="abierto" class="cabecera-movil__velo" @click="abierto = false" />
</template>

<script setup>
  /*
   * La barra azul de la vista movil, con el menu que en escritorio va debajo.
   *
   * No sabe navegar: avisa de lo que se ha elegido y `Header.vue` lo hace con las
   * mismas funciones de siempre, asi que escritorio y movil no pueden acabar
   * llevando a sitios distintos. Se pone con el overlay comun (ver
   * `reemplazos.json`), igual que el resto de lo que le anadimos a la cabecera.
   */
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import router from '@/router'
  import { IconArrowLeft, IconDotsVertical, IconX } from '@tabler/icons-vue'
  import LanguageChange from '@/components/movistar/common/LanguageChange.vue'
  import commonMethods from '@/commonMethods'

  defineProps({
    /** Tipos de experiencia que van en el menu (hoy, los estudios). */
    tipos: { type: Array, default: () => [] }
  })
  const emit = defineEmits(['descubre', 'agenda', 'tipo', 'atencion'])

  const { translate } = commonMethods()
  const route = useRoute()
  const abierto = ref(false)
  const esInicio = computed(() => route.name == 'Home' || route.name == '/')

  const elegir = (que, dato) => {
    abierto.value = false
    emit(que, dato)
  }

  const alPulsarTecla = (evento) => {
    if (evento.key === 'Escape') abierto.value = false
  }
  onMounted(() => document.addEventListener('keydown', alPulsarTecla))
  onBeforeUnmount(() => document.removeEventListener('keydown', alPulsarTecla))

  // Cambiar de pagina por cualquier camino —el boton atras incluido— lo cierra.
  watch(
    () => route.fullPath,
    () => {
      abierto.value = false
    }
  )
</script>

<style lang="scss">
  @import '@/scss/mixings';

  .cabecera-movil {
    position: sticky;
    top: 0;
    // Por encima del contenido y del velo (89), por debajo de los modales (100).
    z-index: 90;
    width: 100%;

    @media screen and (min-width: $sm-breakpoint) {
      display: none;
    }

    // Tres puntos y Espai a la izquierda, Movistar en el centro, idiomas a la
    // derecha. Tres columnas y las de los lados iguales: el logo queda en el
    // centro de verdad aunque un lado sea mas ancho.
    &__barra {
      display: grid;
      grid-template-columns: 1fr auto 1fr;
      align-items: center;
      gap: 6px;
      height: 56px;
      padding: 0 12px 0 2px;
      // El mismo azul que la cabecera de escritorio.
      background: rgb(0, 102, 255);
      color: #fff;
    }

    &__boton {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: none;
      width: 40px;
      height: 44px;
      padding: 0;
      border: 0;
      background: none;
      color: inherit;
    }

    &__izquierda {
      display: flex;
      align-items: center;
      flex: none;
    }

    &__espacio {
      height: 30px;
      cursor: pointer;
    }

    &__logo {
      width: 40px;
      height: auto;
      cursor: pointer;
    }

    &__idioma {
      justify-self: end;
      width: auto;

      .language {
        width: auto;
        font-size: 13px;
      }
    }

    &__menu {
      position: absolute;
      top: 56px;
      left: 0;
      right: 0;
      background: #fff;
      box-shadow: 0 12px 24px rgba(0, 0, 0, 0.18);
    }

    &__opcion {
      display: block;
      width: 100%;
      min-height: 52px;
      padding: 0 20px;
      border: 0;
      border-bottom: 1px solid #ece9e6;
      background: none;
      color: #262423;
      font-family: 'OnAir-text-bold';
      font-size: 16px;
      text-align: left;

      &--activa {
        color: #0066ff;
        font-family: 'OnAir-text-black';
      }
    }

    &__velo {
      position: fixed;
      inset: 0;
      z-index: 89;
      background: rgba(0, 0, 0, 0.35);

      @media screen and (min-width: $sm-breakpoint) {
        display: none;
      }
    }
  }

  // La cabecera que flotaba sobre el banner de la portada —sede, logo grande e
  // idioma— ya la hace la barra, y en medio de la foto estorbaba. Esta en
  // `position: absolute`, asi que quitarla no mueve el banner. Solo en movil,
  // que es donde hay barra. El `!important` es porque los estilos de la portada
  // son `scoped` y le ponen `display: flex` con mas especificidad que esta regla.
  @media screen and (max-width: ($sm-breakpoint - 1px)) {
    .intro-header {
      display: none !important;
    }
  }
</style>
